<template>
  <ModuleWrapper :moduleId="moduleId" :title="uploaderMeta.title">
    <template #main>
      <div class="tw-mb-2 tw-block tw-text-xs">
        {{ uploaderMeta.details }}
      </div>
      <div v-if="logoURLs.length" class="tw-mb-3">
        <div v-for="(url, index) in logoURLs" :key="index" class="tw-group tw-relative tw-mb-3 tw-flex tw-items-center">
          <img
            :src="url"
            :alt="`sponsor logo ${index + 1}`"
            class="tw-mr-2 tw-aspect-video tw-h-8 tw-cursor-pointer tw-rounded tw-object-cover"
            @click="viewLogo(url)"
          />
          <div class="tw-mr-2">
            {{ `${uploaderMeta.nameSuffix}${index + 1}` }}
          </div>
          <template v-if="uploaderMeta.hasDefaults">
            <StudioBadge v-if="index === 0" variation="default"> Default </StudioBadge>
            <button v-else class="tw-cursor-pointer" @click="makeImageFirst(index)">
              <StudioBadge variation="gray"> Make Default </StudioBadge>
            </button>
          </template>
          <button
            class="tw-absolute tw-right-2 tw-top-1.5 tw-hidden tw-cursor-pointer tw-text-deep-orange-600 group-hover:tw-flex"
            @click="onDeleteLogo(index)"
          >
            <TrashCan class="tw-h-5 tw-w-5" />
          </button>
        </div>
      </div>

      <div class="upload-container">
        <FilestackS3
          :s3path="s3BucketPath"
          :acceptedTypes="uploaderMeta.imageTypes"
          :replaceSpacesInFileName="true"
          :imageValidation="uploaderMeta.validation"
          class="tw-w-[120px]"
          @fileuploaded="onImageUpload"
        >
          <StudioButton trackingId="upload-image-button" variant="outline"> Upload Image </StudioButton>
        </FilestackS3>
      </div>
    </template>
  </ModuleWrapper>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';

import { GET_RESOURCES_PATH, LOGO_IMG_VALIDATION, VIRTUAL_BACKGROUND_VALIDATION, STATIC_ASSETS } from '@/constants';
import { StudioBadge, StudioButton } from '@/studioComponents';
import { TrashCan } from '@/studioComponents/Icons';
import { showErrorNotification } from '@/utils/utils.js';

export default {
  name: 'ImageUploaderModule',
  components: {
    ModuleWrapper,
    StudioButton,
    TrashCan,
    StudioBadge
  },
  props: {
    moduleId: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: false,
      default: 'logos'
    }
  },
  data() {
    return {
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`
    };
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    uploaderMeta() {
      if (this.type === 'virtualBackgrounds') {
        return {
          title: 'Virtual Backgrounds',
          nameSuffix: 'Image-',
          hasDefaults: true,
          details: `Give speakers and attendees the option to use a virtual background.
            When a file is uploaded - the virtual backgrounds will appear for everyone
            livestreaming on default. They can then turn it off in their settings modal
            in the control panel.`,
          themeObjectKey: 'virtual_backgrounds',
          validation: VIRTUAL_BACKGROUND_VALIDATION,
          imageTypes: ['image/jpg', 'image/jpeg', 'image/png', 'image/webp']
        };
      }
      return {
        title: 'Onstage images or logos',
        nameSuffix: 'Logo-',
        details:
          'Upload logos or small images to present on stage while the event is live. You’ll be able to select an image to display and the position of the image during the event using the media tab in the sidebar.',
        themeObjectKey: 'logos',
        validation: LOGO_IMG_VALIDATION,
        imageTypes: ['image/*', '.ico']
      };
    },
    logoURLs() {
      const moduleKeys = [this.uploaderMeta.themeObjectKey];
      const logos = this.getInitialModuleData(moduleKeys)[this.uploaderMeta.themeObjectKey];
      return logos ? logos : [];
    }
  },
  methods: {
    ...mapMutations('branding', ['setChangesMadeInThemeObj']),
    onImageUpload(files) {
      const { filesUploaded, filesFailed } = files;
      if (filesFailed?.length !== 0) {
        showErrorNotification('File Upload Failed');
      } else {
        if (filesUploaded?.length > 0) {
          const resourceKey = filesUploaded[0]?.key || '';
          const url = `${STATIC_ASSETS.CLOUDFRONT_URL}/${resourceKey}`;
          this.setChangesMadeInThemeObj({
            [this.uploaderMeta.themeObjectKey]: [...this.logoURLs, url]
          });
        } else {
          showErrorNotification('File Upload Failed');
        }
      }
    },
    onDeleteLogo(index) {
      const logoURLs = [...this.logoURLs];
      logoURLs.splice(index, 1);
      this.setChangesMadeInThemeObj({
        [this.uploaderMeta.themeObjectKey]: [...logoURLs]
      });
    },
    makeImageFirst(index) {
      const logoURLs = [...this.logoURLs];
      const newDefault = logoURLs[index];
      logoURLs.splice(index, 1);
      this.setChangesMadeInThemeObj({
        [this.uploaderMeta.themeObjectKey]: [newDefault, ...logoURLs]
      });
    },
    viewLogo(url) {
      window.open(url, '_blank').focus();
    }
  }
};
</script>
